/**
 * Task Estimation CRUD Service - Phase 4 Architecture
 *
 * Encapsulates core CRUD operations for task estimations.
 * Extracted from TaskEstimationService to reduce single-responsibility violations.
 */

import { typedSupabase } from '@/lib/typed-supabase';
import { logger } from '@/lib/logger';
import {
  IService,
  ServiceResponse,
  createSuccessResponse,
  createErrorResponse,
} from '@/services/core/service-registry';
import type {
  CreateTaskEstimationRequest,
  TaskEstimation,
  UpdateTaskEstimationRequest,
  EstimationFactor,
} from "@/types/time-management";

export class TaskEstimationCrudService implements IService {
  public readonly name = "TaskEstimationCrudService";
  private readonly supabase = typedSupabase;

  async initialize(): Promise<void> {
    logger.info("✅ TaskEstimationCrudService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("✅ TaskEstimationCrudService disposed");
  }

  async createEstimation(request: CreateTaskEstimationRequest): Promise<ServiceResponse<TaskEstimation>> {
    try {
      const { data, error } = await this.supabase
        .from("task_estimations")
        .insert({
          task_id: request.task_id,
          estimated_duration: request.estimated_duration,
          confidence_level: request.confidence_level,
          estimation_method: request.estimation_method,
          factors: request.factors
            ? JSON.parse(JSON.stringify(request.factors))
            : null,
          notes: request.notes,
        })
        .select()
        .single();

      if (error) {
        return createErrorResponse(error.message);
      }

      return createSuccessResponse({
        ...data,
        factors: data.factors
          ? (data.factors as unknown as EstimationFactor[])
          : [],
      } as TaskEstimation);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async getEstimation(estimationId: string): Promise<ServiceResponse<TaskEstimation | null>> {
    try {
      const { data, error } = await this.supabase
        .from("task_estimations")
        .select("*")
        .eq("id", estimationId)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          return createSuccessResponse(null);
        }
        return createErrorResponse(error.message);
      }

      return createSuccessResponse({
        ...data,
        factors: data.factors
          ? (data.factors as unknown as EstimationFactor[])
          : [],
      } as TaskEstimation);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async getTaskEstimations(taskId: string): Promise<ServiceResponse<TaskEstimation[]>> {
    try {
      const { data, error } = await this.supabase
        .from("task_estimations")
        .select("*")
        .eq("task_id", taskId)
        .order("created_at", { ascending: false });

      if (error) {
        return createErrorResponse(error.message);
      }

      const processedData = (data || []).map(
        (item: {
          id: string;
          task_id: string;
          estimated_duration: number;
          confidence_level: string;
          estimation_method: string;
          factors: unknown;
          notes: string | null;
          actual_duration: number | null;
          created_at: string | null;
          updated_at: string | null;
        }) => ({
          ...item,
          factors: item.factors
            ? (item.factors as unknown as EstimationFactor[])
            : [],
        })
      ) as TaskEstimation[];

      return createSuccessResponse(processedData);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async updateEstimation(
    estimationId: string,
    updates: UpdateTaskEstimationRequest
  ): Promise<ServiceResponse<TaskEstimation>> {
    try {
      const { data, error } = await this.supabase
        .from("task_estimations")
        .update({
          ...updates,
          factors: updates.factors
            ? JSON.parse(JSON.stringify(updates.factors))
            : undefined,
          updated_at: new Date().toISOString(),
        })
        .eq("id", estimationId)
        .select()
        .single();

      if (error) {
        return createErrorResponse(error.message);
      }

      return createSuccessResponse({
        ...data,
        factors: data.factors
          ? (data.factors as unknown as EstimationFactor[])
          : [],
      } as TaskEstimation);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async deleteEstimation(estimationId: string): Promise<ServiceResponse<void>> {
    try {
      const { error } = await this.supabase
        .from("task_estimations")
        .delete()
        .eq("id", estimationId);

      if (error) {
        return createErrorResponse(error.message);
      }

      return createSuccessResponse(undefined);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }
}
